import { createInstance } from 'i18next';

import type { AppLocale } from '../locale';

import { APP_LOCALES, getActiveInterfaceLocale } from '../locale';
import { ACCOUNT_TRANSLATIONS } from './account';
import { AUTH_TRANSLATIONS } from './auth';
import { BOOSTERS_TRANSLATIONS } from './boosters';
import { CREATE_PROJECT_TRANSLATIONS } from './createProject';
import { EDITOR_TRANSLATIONS } from './editor';
import { HOME_TRANSLATIONS } from './home';
import { PRICING_TRANSLATIONS } from './pricing';
import { PT_PT_TRANSLATIONS } from './pt-PT';
import { SHELL_TRANSLATIONS } from './shell';
import { TRIAL_TRANSLATIONS } from './trial';
import { TRIAL_PAYWALL_TRANSLATIONS } from './trialPaywall';
import { VOICE_TRANSLATIONS } from './voice';

export const EN_TRANSLATIONS = {
  'account.settings': 'Account settings',
  'common.advancedTools': 'Advanced tools',
  'common.back': 'Back',
  'common.desktopOnly': 'Desktop only',
  'common.fieldRequired': 'Field is required',
  'common.free': 'Free',
  'common.hideSidebar': 'Hide sidebar',
  'common.lockSidebar': 'Lock sidebar',
  'common.logout': 'Log out',
  'common.navigationSidebar': 'Navigation sidebar',
  'common.openSidebar': 'open sidebar',
  'common.or': 'or',
  'common.profile': 'Profile',
  'common.profileImage': 'Profile image',
  'common.serverError': 'Server error. Please try again later',
  'header.myProfile': 'My profile',
  'header.profileAndApi': 'Profile & API',
  'home.newFolder': 'New folder',
  'home.projects': 'Projects',
  'home.refreshPage': 'Refresh page',
  'home.searchVideos': 'Search your videos',
  'home.somethingWentWrong': 'Something went wrong',
  'home.translateVideoAndAudio': 'Translate video and audio',
  'home.trial.day': 'Day {currentDay} of {totalDays}',
  'home.trial.dayAria': 'Trial day {currentDay} of {totalDays}',
  'home.trial.dayLeft': '{count} day left to explore',
  'home.trial.daysLeft': '{count} days left to explore',
  'home.trial.description': "Let's localize your first video. Start a chat whenever you'd like a hand.",
  'home.trial.finalDay': 'Final trial day',
  'home.trial.noCreditCard': 'No credit card. No commitment.',
  'home.trial.videoLeft': '{count} video left',
  'home.trial.videosLeft': '{count} videos left',
  'home.trial.welcome': 'Welcome to Rask',
  'home.tryRefresh': 'Please refresh the page',
  'language.english': 'English',
  'language.french': 'French',
  'language.italian': 'Italian',
  'language.label': 'Interface language',
  'language.portuguese': 'Portuguese (Brazil)',
  'language.portuguesePortugal': 'Portuguese (Portugal)',
  'language.russian': 'Russian',
  'language.spanish': 'Spanish',
  'sidebar.bookCall': 'Book a call with Customer Success',
  'sidebar.dictionary': 'Dictionary',
  'sidebar.getHelp': 'Get help',
  'sidebar.helpCenter': 'Help Center',
  'sidebar.settingsMembers': 'Settings',
  'sidebar.translation': 'Translation',
  'sidebar.viewPlans': 'View plans',
  'sidebar.voiceCloneLibrary': 'Voice Clone',
  'createProject.add': 'Add',
  'createProject.autodetect': 'Autodetect',
  'createProject.choose': 'Choose',
  'createProject.chooseFile': 'Click to choose a file',
  'createProject.chooseFiles': 'Click to choose up to {count} files',
  'createProject.dragIt': 'or drag and drop it here',
  'createProject.dragThem': 'or drag and drop them here',
  'createProject.dropFiles': 'Drop your files or click to upload',
  'createProject.fillDetails': 'Fill in your project details',
  'createProject.fillDetailsAndUpload': 'Fill in your project details and upload files',
  'createProject.firstVideoDescription': 'Your video, speaking any language.',
  'createProject.firstVideoTitle': 'Localize your first video',
  'createProject.freeVideoLeft': '{count} free video',
  'createProject.freeVideosLeft': '{count} free videos',
  'createProject.localize': 'Localize',
  'createProject.localizeInto': 'Localize into',
  'createProject.localizeNow': 'Localize now',
  'createProject.mainTool': 'Main tool',
  'createProject.newProject': 'New project',
  'createProject.newTranslation': 'New translation',
  'createProject.originalLanguage': 'Original language',
  'createProject.pasteLink': 'Paste a YouTube or Google Drive link',
  'createProject.pasteLinkWithVimeo': 'Paste a YouTube, Google Drive, or Vimeo link',
  'createProject.projectName': 'Project name',
  'createProject.recordDescription':
    'Capture screen and camera. Rask cleans up speech, adds zooms, and delivers a finished video.',
  'createProject.recordTitle': 'Record screen, get a polished video',
  'createProject.renewPlan': 'Renew plan',
  'createProject.sampleDescription':
    "We've already filled in the details about the sample video. Just choose the language you want to translate the video into.",
  'createProject.selectLanguages': 'Select the language of the original video and the language you will translate into',
  'createProject.selectTargetLanguage': 'Select target language for video translation',
  'createProject.translate': 'Translate',
  'createProject.translateCardTitle': 'Translate Video or Audio',
  'createProject.translateDescription':
    'Upload any video or audio file and translate it into 165+ languages and dialects.',
  'createProject.translateFirstMinute': "We'll localize the first minute of your video.",
  'createProject.translateFirstMinuteRest': "Longer is fine — we'll use the first minute for now.",
  'createProject.translateNow': 'Translate now',
  'createProject.translateRecording': 'Translate your recording',
  'createProject.translateTo': 'Translate to',
  'createProject.trialUploadDescription': "Upload a file or paste a link, pick a language, and we'll handle the rest.",
  'createProject.trySample': 'try a sample',
  'createProject.trySampleVideo': "Don't have a clip handy? Try a sample video",
  'createProject.upgradePlan': 'Upgrade plan',
  'createProject.uploadFile': 'Click to upload a file',
  'createProject.uploadTitle': 'Upload video or audio to translate',
  'createProject.videoLeft': '{count} video left',
  'createProject.videoName': 'Video name',
  'createProject.videoNames': 'Video names',
  'createProject.videosLeft': '{count} videos left',
  'createProject.videoSpeakers': 'Number of speakers in video',
  'createProject.zeroDescription': 'Get clean subtitles, natural voiceover, and perfect timing in one tool',
  'createProject.zeroTitle': 'Translate video or audio to 135+ languages',
  ...ACCOUNT_TRANSLATIONS.en,
  ...AUTH_TRANSLATIONS.en,
  ...BOOSTERS_TRANSLATIONS.en,
  ...CREATE_PROJECT_TRANSLATIONS.en,
  ...EDITOR_TRANSLATIONS.en,
  ...HOME_TRANSLATIONS.en,
  ...PRICING_TRANSLATIONS.en,
  ...SHELL_TRANSLATIONS.en,
  ...TRIAL_TRANSLATIONS.en,
  ...TRIAL_PAYWALL_TRANSLATIONS.en,
  ...VOICE_TRANSLATIONS.en,
} as const;

export type TranslationKey = keyof typeof EN_TRANSLATIONS;
export type Translate = (key: TranslationKey, variables?: Record<string, number | string>) => string;
type TranslationDictionary = Record<TranslationKey, string>;

const ES_TRANSLATIONS: TranslationDictionary = {
  'account.settings': 'Configuración de la cuenta',
  'common.advancedTools': 'Herramientas avanzadas',
  'common.back': 'Atrás',
  'common.desktopOnly': 'Solo en ordenador',
  'common.fieldRequired': 'Campo obligatorio',
  'common.free': 'Gratis',
  'common.hideSidebar': 'Ocultar barra lateral',
  'common.lockSidebar': 'Fijar barra lateral',
  'common.logout': 'Cerrar sesión',
  'common.navigationSidebar': 'Barra lateral de navegación',
  'common.openSidebar': 'Abrir barra lateral',
  'common.or': 'o',
  'common.profile': 'Perfil',
  'common.profileImage': 'Imagen de perfil',
  'common.serverError': 'Error del servidor. Inténtalo de nuevo más tarde',
  'header.myProfile': 'Mi perfil',
  'header.profileAndApi': 'Perfil y API',
  'home.newFolder': 'Nueva carpeta',
  'home.projects': 'Proyectos',
  'home.refreshPage': 'Actualizar página',
  'home.searchVideos': 'Buscar en tus videos',
  'home.somethingWentWrong': 'Algo salió mal',
  'home.translateVideoAndAudio': 'Traduce video y audio',
  'home.trial.day': 'Día {currentDay} de {totalDays}',
  'home.trial.dayAria': 'Día {currentDay} de {totalDays} de la prueba',
  'home.trial.dayLeft': 'Queda {count} día para explorar',
  'home.trial.daysLeft': 'Quedan {count} días para explorar',
  'home.trial.description': 'Adapta tu primer video. Inicia un chat cuando necesites ayuda.',
  'home.trial.finalDay': 'Último día de prueba',
  'home.trial.noCreditCard': 'Sin tarjeta de crédito. Sin compromiso.',
  'home.trial.videoLeft': 'Queda {count} video',
  'home.trial.videosLeft': 'Quedan {count} videos',
  'home.trial.welcome': 'Te damos la bienvenida a Rask',
  'home.tryRefresh': 'Actualiza la página',
  'language.english': 'Inglés',
  'language.french': 'Francés',
  'language.italian': 'Italiano',
  'language.label': 'Idioma de la interfaz',
  'language.portuguese': 'Portugués (Brasil)',
  'language.portuguesePortugal': 'Portugués (Portugal)',
  'language.russian': 'Ruso',
  'language.spanish': 'Español',
  'sidebar.bookCall': 'Reservar una llamada con el equipo de Éxito del Cliente',
  'sidebar.dictionary': 'Diccionario',
  'sidebar.getHelp': 'Obtener ayuda',
  'sidebar.helpCenter': 'Centro de ayuda',
  'sidebar.settingsMembers': 'Configuración',
  'sidebar.translation': 'Traducción',
  'sidebar.viewPlans': 'Ver planes',
  'sidebar.voiceCloneLibrary': 'Voice Clone',
  'createProject.add': 'Añadir',
  'createProject.autodetect': 'Detección automática',
  'createProject.choose': 'Elegir',
  'createProject.chooseFile': 'Haz clic para elegir un archivo',
  'createProject.chooseFiles': 'Haz clic para elegir hasta {count} archivos',
  'createProject.dragIt': 'o arrástralo y suéltalo aquí',
  'createProject.dragThem': 'o arrástralos y suéltalos aquí',
  'createProject.dropFiles': 'Arrastra tus archivos o haz clic para subirlos',
  'createProject.fillDetails': 'Completa los detalles del proyecto',
  'createProject.fillDetailsAndUpload': 'Completa los detalles del proyecto y sube los archivos',
  'createProject.firstVideoDescription': 'Tu video, hablando en cualquier idioma.',
  'createProject.firstVideoTitle': 'Adapta tu primer video',
  'createProject.freeVideoLeft': '{count} video gratis',
  'createProject.freeVideosLeft': '{count} videos gratis',
  'createProject.localize': 'Adaptar',
  'createProject.localizeInto': 'Adaptar a',
  'createProject.localizeNow': 'Adaptar ahora',
  'createProject.mainTool': 'Herramienta principal',
  'createProject.newProject': 'Nuevo proyecto',
  'createProject.newTranslation': 'Nueva traducción',
  'createProject.originalLanguage': 'Idioma original',
  'createProject.pasteLink': 'Pega un enlace de YouTube o Google Drive',
  'createProject.pasteLinkWithVimeo': 'Pega un enlace de YouTube, Google Drive o Vimeo',
  'createProject.projectName': 'Nombre del proyecto',
  'createProject.recordDescription':
    'Captura la pantalla y la cámara. Rask limpia el audio, añade zooms y entrega un video terminado.',
  'createProject.recordTitle': 'Graba la pantalla y obtén un video profesional',
  'createProject.renewPlan': 'Renovar plan',
  'createProject.sampleDescription':
    'Ya hemos completado los detalles del video de muestra. Solo tienes que elegir el idioma al que quieres traducirlo.',
  'createProject.selectLanguages': 'Selecciona el idioma original y el idioma al que quieres traducir',
  'createProject.selectTargetLanguage': 'Selecciona el idioma de destino de la traducción',
  'createProject.translate': 'Traducir',
  'createProject.translateCardTitle': 'Traducir video o audio',
  'createProject.translateDescription':
    'Sube cualquier archivo de video o audio y tradúcelo a más de 165 idiomas y dialectos.',
  'createProject.translateFirstMinute': 'Adaptaremos el primer minuto de tu video.',
  'createProject.translateFirstMinuteRest': 'No importa si es más largo: por ahora usaremos el primer minuto.',
  'createProject.translateNow': 'Traducir ahora',
  'createProject.translateRecording': 'Traduce tu grabación',
  'createProject.translateTo': 'Traducir a',
  'createProject.trialUploadDescription':
    'Sube un archivo o pega un enlace, elige un idioma y nosotros nos encargamos del resto.',
  'createProject.trySample': 'prueba una muestra',
  'createProject.trySampleVideo': '¿No tienes un video a mano? Prueba con uno de nuestros ejemplos',
  'createProject.upgradePlan': 'Cambiar a un plan superior',
  'createProject.uploadFile': 'Haz clic para subir un archivo',
  'createProject.uploadTitle': 'Sube un video o audio para traducir',
  'createProject.videoLeft': 'Queda {count} video',
  'createProject.videoName': 'Nombre del video',
  'createProject.videoNames': 'Nombres de los videos',
  'createProject.videosLeft': 'Quedan {count} videos',
  'createProject.videoSpeakers': 'Número de hablantes en el video',
  'createProject.zeroDescription':
    'Obtén subtítulos limpios, locuciones naturales y una sincronización perfecta, todo en una sola herramienta',
  'createProject.zeroTitle': 'Traduce video o audio a más de 135 idiomas',
  ...ACCOUNT_TRANSLATIONS.es,
  ...AUTH_TRANSLATIONS.es,
  ...BOOSTERS_TRANSLATIONS.es,
  ...CREATE_PROJECT_TRANSLATIONS.es,
  ...EDITOR_TRANSLATIONS.es,
  ...HOME_TRANSLATIONS.es,
  ...PRICING_TRANSLATIONS.es,
  ...SHELL_TRANSLATIONS.es,
  ...TRIAL_TRANSLATIONS.es,
  ...TRIAL_PAYWALL_TRANSLATIONS.es,
  ...VOICE_TRANSLATIONS.es,
};

const PT_TRANSLATIONS: TranslationDictionary = {
  'account.settings': 'Configurações da conta',
  'common.advancedTools': 'Ferramentas avançadas',
  'common.back': 'Voltar',
  'common.desktopOnly': 'Apenas no computador',
  'common.fieldRequired': 'Campo obrigatório',
  'common.free': 'Grátis',
  'common.hideSidebar': 'Ocultar barra lateral',
  'common.lockSidebar': 'Fixar barra lateral',
  'common.logout': 'Sair',
  'common.navigationSidebar': 'Barra lateral de navegação',
  'common.openSidebar': 'Abrir barra lateral',
  'common.or': 'ou',
  'common.profile': 'Perfil',
  'common.profileImage': 'Imagem do perfil',
  'common.serverError': 'Erro no servidor. Tente novamente mais tarde',
  'header.myProfile': 'Meu perfil',
  'header.profileAndApi': 'Perfil e API',
  'home.newFolder': 'Nova pasta',
  'home.projects': 'Projetos',
  'home.refreshPage': 'Atualizar página',
  'home.searchVideos': 'Pesquisar seus vídeos',
  'home.somethingWentWrong': 'Algo deu errado',
  'home.translateVideoAndAudio': 'Traduzir vídeo e áudio',
  'home.trial.day': 'Dia {currentDay} de {totalDays}',
  'home.trial.dayAria': 'Dia {currentDay} de {totalDays} do teste',
  'home.trial.dayLeft': 'Resta {count} dia para explorar',
  'home.trial.daysLeft': 'Restam {count} dias para explorar',
  'home.trial.description': 'Adapte seu primeiro vídeo. Inicie um chat sempre que precisar de ajuda.',
  'home.trial.finalDay': 'Último dia do teste',
  'home.trial.noCreditCard': 'Sem cartão de crédito. Sem compromisso.',
  'home.trial.videoLeft': 'Resta {count} vídeo',
  'home.trial.videosLeft': 'Restam {count} vídeos',
  'home.trial.welcome': 'Bem-vindo ao Rask',
  'home.tryRefresh': 'Atualize a página',
  'language.english': 'Inglês',
  'language.french': 'Francês',
  'language.italian': 'Italiano',
  'language.label': 'Idioma da interface',
  'language.portuguese': 'Português (Brasil)',
  'language.portuguesePortugal': 'Português (Portugal)',
  'language.russian': 'Russo',
  'language.spanish': 'Espanhol',
  'sidebar.bookCall': 'Agendar uma chamada com a equipe de Sucesso do Cliente',
  'sidebar.dictionary': 'Dicionário',
  'sidebar.getHelp': 'Obter ajuda',
  'sidebar.helpCenter': 'Central de ajuda',
  'sidebar.settingsMembers': 'Configurações',
  'sidebar.translation': 'Tradução',
  'sidebar.viewPlans': 'Ver planos',
  'sidebar.voiceCloneLibrary': 'Voice Clone',
  'createProject.add': 'Adicionar',
  'createProject.autodetect': 'Detecção automática',
  'createProject.choose': 'Escolher',
  'createProject.chooseFile': 'Clique para escolher um arquivo',
  'createProject.chooseFiles': 'Clique para escolher até {count} arquivos',
  'createProject.dragIt': 'ou arraste-o e solte-o aqui',
  'createProject.dragThem': 'ou arraste-os e solte-os aqui',
  'createProject.dropFiles': 'Solte seus arquivos aqui ou clique para enviar',
  'createProject.fillDetails': 'Preencha os detalhes do projeto',
  'createProject.fillDetailsAndUpload': 'Preencha os detalhes do projeto e envie os arquivos',
  'createProject.firstVideoDescription': 'Seu vídeo falando em qualquer idioma.',
  'createProject.firstVideoTitle': 'Adapte seu primeiro vídeo',
  'createProject.freeVideoLeft': '{count} vídeo grátis',
  'createProject.freeVideosLeft': '{count} vídeos grátis',
  'createProject.localize': 'Adaptar',
  'createProject.localizeInto': 'Adaptar para',
  'createProject.localizeNow': 'Adaptar agora',
  'createProject.mainTool': 'Ferramenta principal',
  'createProject.newProject': 'Novo projeto',
  'createProject.newTranslation': 'Nova tradução',
  'createProject.originalLanguage': 'Idioma original',
  'createProject.pasteLink': 'Adicione um link do YouTube ou Google Drive',
  'createProject.pasteLinkWithVimeo': 'Cole um link do YouTube, Google Drive ou Vimeo',
  'createProject.projectName': 'Nome do projeto',
  'createProject.recordDescription':
    'Capture a tela e a câmera. O Rask limpa a fala, adiciona zooms e entrega um vídeo finalizado.',
  'createProject.recordTitle': 'Grave a tela e obtenha um vídeo profissional',
  'createProject.renewPlan': 'Renovar plano',
  'createProject.sampleDescription':
    'Já preenchemos os detalhes do vídeo de exemplo. Basta escolher o idioma para o qual deseja traduzi-lo.',
  'createProject.selectLanguages': 'Selecione o idioma original e o idioma para o qual deseja traduzir',
  'createProject.selectTargetLanguage': 'Selecione o idioma de destino da tradução',
  'createProject.translate': 'Traduzir',
  'createProject.translateCardTitle': 'Traduzir vídeo ou áudio',
  'createProject.translateDescription':
    'Envie qualquer arquivo de vídeo ou áudio e traduza-o para mais de 165 idiomas e dialetos.',
  'createProject.translateFirstMinute': 'Vamos adaptar o primeiro minuto do seu vídeo.',
  'createProject.translateFirstMinuteRest':
    'Não há problema se for mais longo — usaremos o primeiro minuto por enquanto.',
  'createProject.translateNow': 'Traduzir agora',
  'createProject.translateRecording': 'Traduza sua gravação',
  'createProject.translateTo': 'Traduzir para',
  'createProject.trialUploadDescription':
    'Envie um arquivo ou cole um link, escolha um idioma e cuidaremos do restante.',
  'createProject.trySample': 'teste uma amostra',
  'createProject.trySampleVideo': 'Não tem um clipe? Teste um vídeo de exemplo',
  'createProject.upgradePlan': 'Fazer upgrade do plano',
  'createProject.uploadFile': 'Clique para enviar um arquivo',
  'createProject.uploadTitle': 'Envie um vídeo ou áudio para traduzir',
  'createProject.videoLeft': 'Resta {count} vídeo',
  'createProject.videoName': 'Nome do vídeo',
  'createProject.videoNames': 'Nomes dos vídeos',
  'createProject.videosLeft': 'Restam {count} vídeos',
  'createProject.videoSpeakers': 'Número de pessoas falando no vídeo',
  'createProject.zeroDescription':
    'Obtenha legendas limpas, narração natural e sincronia perfeita em uma só ferramenta',
  'createProject.zeroTitle': 'Traduza vídeo ou áudio para mais de 135 idiomas',
  ...ACCOUNT_TRANSLATIONS.pt,
  ...AUTH_TRANSLATIONS.pt,
  ...BOOSTERS_TRANSLATIONS.pt,
  ...CREATE_PROJECT_TRANSLATIONS.pt,
  ...EDITOR_TRANSLATIONS.pt,
  ...HOME_TRANSLATIONS.pt,
  ...PRICING_TRANSLATIONS.pt,
  ...SHELL_TRANSLATIONS.pt,
  ...TRIAL_TRANSLATIONS.pt,
  ...TRIAL_PAYWALL_TRANSLATIONS.pt,
  ...VOICE_TRANSLATIONS.pt,
};

const FR_TRANSLATIONS: TranslationDictionary = {
  'account.settings': 'Paramètres du compte',
  'common.advancedTools': 'Outils avancés',
  'common.back': 'Retour',
  'common.desktopOnly': 'Sur ordinateur uniquement',
  'common.fieldRequired': 'Champ obligatoire',
  'common.free': 'Gratuit',
  'common.hideSidebar': 'Masquer la barre latérale',
  'common.lockSidebar': 'Verrouiller la barre latérale',
  'common.logout': 'Se déconnecter',
  'common.navigationSidebar': 'Barre latérale de navigation',
  'common.openSidebar': 'Ouvrir la barre latérale',
  'common.or': 'ou',
  'common.profile': 'Profil',
  'common.profileImage': 'Image de profil',
  'common.serverError': 'Erreur du serveur. Réessayez plus tard',
  'header.myProfile': 'Mon profil',
  'header.profileAndApi': 'Profil et API',
  'home.newFolder': 'Nouveau dossier',
  'home.projects': 'Projets',
  'home.refreshPage': 'Actualiser la page',
  'home.searchVideos': 'Rechercher dans vos vidéos',
  'home.somethingWentWrong': "Une erreur s'est produite",
  'home.translateVideoAndAudio': 'Traduisez des vidéos et des fichiers audio',
  'home.trial.day': 'Jour {currentDay} sur {totalDays}',
  'home.trial.dayAria': "Jour {currentDay} sur {totalDays} de l'essai",
  'home.trial.dayLeft': 'Encore {count} jour pour découvrir',
  'home.trial.daysLeft': 'Encore {count} jours pour découvrir',
  'home.trial.description': 'Adaptez votre première vidéo. Lancez une discussion dès que vous avez besoin d’aide.',
  'home.trial.finalDay': "Dernier jour d'essai",
  'home.trial.noCreditCard': 'Sans carte bancaire. Sans engagement.',
  'home.trial.videoLeft': 'Il reste {count} vidéo',
  'home.trial.videosLeft': 'Il reste {count} vidéos',
  'home.trial.welcome': 'Bienvenue sur Rask',
  'home.tryRefresh': 'Actualisez la page',
  'language.english': 'Anglais',
  'language.french': 'Français',
  'language.italian': 'Italien',
  'language.label': "Langue de l'interface",
  'language.portuguese': 'Portugais (Brésil)',
  'language.portuguesePortugal': 'Portugais (Portugal)',
  'language.russian': 'Russe',
  'language.spanish': 'Espagnol',
  'sidebar.bookCall': "Planifier un appel avec l'équipe CS",
  'sidebar.dictionary': 'Dictionnaire',
  'sidebar.getHelp': "Obtenir de l'aide",
  'sidebar.helpCenter': "Centre d'aide",
  'sidebar.settingsMembers': 'Paramètres',
  'sidebar.translation': 'Traduction',
  'sidebar.viewPlans': 'Voir les offres',
  'sidebar.voiceCloneLibrary': 'Voice Clone',
  'createProject.add': 'Ajouter',
  'createProject.autodetect': 'Détection automatique',
  'createProject.choose': 'Choisir',
  'createProject.chooseFile': 'Cliquez pour choisir un fichier',
  'createProject.chooseFiles': "Cliquez pour choisir jusqu'à {count} fichiers",
  'createProject.dragIt': 'ou glissez-déposez-le ici',
  'createProject.dragThem': 'ou glissez-déposez-les ici',
  'createProject.dropFiles': 'Déposez vos fichiers ou cliquez pour les importer',
  'createProject.fillDetails': 'Renseignez les détails de votre projet',
  'createProject.fillDetailsAndUpload': 'Renseignez les détails du projet et importez vos fichiers',
  'createProject.firstVideoDescription': "Votre vidéo, dans n'importe quelle langue.",
  'createProject.firstVideoTitle': 'Adaptez votre première vidéo',
  'createProject.freeVideoLeft': '{count} vidéo gratuite',
  'createProject.freeVideosLeft': '{count} vidéos gratuites',
  'createProject.localize': 'Adapter',
  'createProject.localizeInto': 'Adapter en',
  'createProject.localizeNow': 'Adapter maintenant',
  'createProject.mainTool': 'Outil principal',
  'createProject.newProject': 'Nouveau projet',
  'createProject.newTranslation': 'Nouvelle traduction',
  'createProject.originalLanguage': "Langue d'origine",
  'createProject.pasteLink': 'Collez un lien YouTube ou Google Drive',
  'createProject.pasteLinkWithVimeo': 'Collez un lien YouTube, Google Drive ou Vimeo',
  'createProject.projectName': 'Nom du projet',
  'createProject.recordDescription':
    "Capturez l'écran et la caméra. Rask nettoie la voix, ajoute des zooms et livre une vidéo finalisée.",
  'createProject.recordTitle': "Enregistrez l'écran et obtenez une vidéo soignée",
  'createProject.renewPlan': "Renouveler l'offre",
  'createProject.sampleDescription':
    "Nous avons déjà renseigné les informations concernant la vidéo d'exemple. Choisissez simplement la langue dans laquelle vous souhaitez la traduire.",
  'createProject.selectLanguages': "Sélectionnez la langue d'origine et la langue de traduction",
  'createProject.selectTargetLanguage': 'Sélectionnez la langue cible de la traduction',
  'createProject.translate': 'Traduire',
  'createProject.translateCardTitle': 'Traduire une vidéo ou un fichier audio',
  'createProject.translateDescription':
    'Importez un fichier vidéo ou audio et traduisez-le dans plus de 165 langues et dialectes.',
  'createProject.translateFirstMinute': 'Nous adapterons la première minute de votre vidéo.',
  'createProject.translateFirstMinuteRest':
    'La vidéo peut être plus longue : pour le moment, nous utiliserons la première minute.',
  'createProject.translateNow': 'Traduire maintenant',
  'createProject.translateRecording': 'Traduisez votre enregistrement',
  'createProject.translateTo': 'Traduire en',
  'createProject.trialUploadDescription':
    'Importez un fichier ou collez un lien, choisissez une langue et nous nous occupons du reste.',
  'createProject.trySample': 'essayez un exemple',
  'createProject.trySampleVideo': "Vous n'avez pas de vidéo sous la main ? Essayez une vidéo d'exemple",
  'createProject.upgradePlan': 'Passer à une offre supérieure',
  'createProject.uploadFile': 'Cliquez pour importer un fichier',
  'createProject.uploadTitle': 'Importez une vidéo ou un fichier audio à traduire',
  'createProject.videoLeft': 'Il reste {count} vidéo',
  'createProject.videoName': 'Nom de la vidéo',
  'createProject.videoNames': 'Noms des vidéos',
  'createProject.videosLeft': 'Il reste {count} vidéos',
  'createProject.videoSpeakers': 'Nombre de locuteurs dans la vidéo',
  'createProject.zeroDescription':
    'Obtenez des sous-titres nets, une voix off naturelle et une synchronisation parfaite dans un seul outil',
  'createProject.zeroTitle': 'Traduisez une vidéo ou un fichier audio dans plus de 135 langues',
  ...ACCOUNT_TRANSLATIONS.fr,
  ...AUTH_TRANSLATIONS.fr,
  ...BOOSTERS_TRANSLATIONS.fr,
  ...CREATE_PROJECT_TRANSLATIONS.fr,
  ...EDITOR_TRANSLATIONS.fr,
  ...HOME_TRANSLATIONS.fr,
  ...PRICING_TRANSLATIONS.fr,
  ...SHELL_TRANSLATIONS.fr,
  ...TRIAL_TRANSLATIONS.fr,
  ...TRIAL_PAYWALL_TRANSLATIONS.fr,
  ...VOICE_TRANSLATIONS.fr,
};

const IT_TRANSLATIONS: TranslationDictionary = {
  'account.settings': "Impostazioni dell'account",
  'common.advancedTools': 'Strumenti avanzati',
  'common.back': 'Indietro',
  'common.desktopOnly': 'Solo su computer',
  'common.fieldRequired': 'Campo obbligatorio',
  'common.free': 'Gratis',
  'common.hideSidebar': 'Nascondi barra laterale',
  'common.lockSidebar': 'Blocca barra laterale',
  'common.logout': 'Esci',
  'common.navigationSidebar': 'Barra laterale di navigazione',
  'common.openSidebar': 'Apri barra laterale',
  'common.or': 'o',
  'common.profile': 'Profilo',
  'common.profileImage': 'Immagine del profilo',
  'common.serverError': 'Errore del server. Riprova più tardi',
  'header.myProfile': 'Il mio profilo',
  'header.profileAndApi': 'Profilo e API',
  'home.newFolder': 'Nuova cartella',
  'home.projects': 'Progetti',
  'home.refreshPage': 'Aggiorna pagina',
  'home.searchVideos': 'Cerca nei tuoi video',
  'home.somethingWentWrong': 'Qualcosa è andato storto',
  'home.translateVideoAndAudio': 'Traduci video e audio',
  'home.trial.day': 'Giorno {currentDay} di {totalDays}',
  'home.trial.dayAria': 'Giorno {currentDay} di {totalDays} della prova',
  'home.trial.dayLeft': 'Rimane {count} giorno per esplorare',
  'home.trial.daysLeft': 'Rimangono {count} giorni per esplorare',
  'home.trial.description': 'Localizza il tuo primo video. Avvia una chat quando hai bisogno di aiuto.',
  'home.trial.finalDay': 'Ultimo giorno di prova',
  'home.trial.noCreditCard': 'Nessuna carta di credito. Nessun impegno.',
  'home.trial.videoLeft': 'Rimane {count} video',
  'home.trial.videosLeft': 'Rimangono {count} video',
  'home.trial.welcome': 'Ti diamo il benvenuto su Rask',
  'home.tryRefresh': 'Aggiorna la pagina',
  'language.english': 'Inglese',
  'language.french': 'Francese',
  'language.italian': 'Italiano',
  'language.label': "Lingua dell'interfaccia",
  'language.portuguese': 'Portoghese (Brasile)',
  'language.portuguesePortugal': 'Portoghese (Portogallo)',
  'language.russian': 'Russo',
  'language.spanish': 'Spagnolo',
  'sidebar.bookCall': 'Prenota una chiamata con il team Customer Success',
  'sidebar.dictionary': 'Dizionario',
  'sidebar.getHelp': 'Richiedi assistenza',
  'sidebar.helpCenter': 'Centro assistenza',
  'sidebar.settingsMembers': 'Impostazioni',
  'sidebar.translation': 'Traduzione',
  'sidebar.viewPlans': 'Visualizza i piani',
  'sidebar.voiceCloneLibrary': 'Voice Clone',
  'createProject.add': 'Aggiungi',
  'createProject.autodetect': 'Rilevamento automatico',
  'createProject.choose': 'Scegli',
  'createProject.chooseFile': 'Fai clic per scegliere un file',
  'createProject.chooseFiles': 'Fai clic per scegliere fino a {count} file',
  'createProject.dragIt': 'oppure trascinalo qui',
  'createProject.dragThem': 'oppure trascinali qui',
  'createProject.dropFiles': 'Trascina i file o fai clic per caricarli',
  'createProject.fillDetails': 'Inserisci i dettagli del progetto',
  'createProject.fillDetailsAndUpload': 'Inserisci i dettagli del progetto e carica i file',
  'createProject.firstVideoDescription': 'Il tuo video, in qualsiasi lingua.',
  'createProject.firstVideoTitle': 'Localizza il tuo primo video',
  'createProject.freeVideoLeft': '{count} video gratuito',
  'createProject.freeVideosLeft': '{count} video gratuiti',
  'createProject.localize': 'Localizza',
  'createProject.localizeInto': 'Localizza in',
  'createProject.localizeNow': 'Localizza ora',
  'createProject.mainTool': 'Strumento principale',
  'createProject.newProject': 'Nuovo progetto',
  'createProject.newTranslation': 'Nuova traduzione',
  'createProject.originalLanguage': 'Lingua originale',
  'createProject.pasteLink': 'Incolla un link di YouTube o Google Drive',
  'createProject.pasteLinkWithVimeo': 'Incolla un link di YouTube, Google Drive o Vimeo',
  'createProject.projectName': 'Nome del progetto',
  'createProject.recordDescription':
    'Registra schermo e videocamera. Rask pulisce il parlato, aggiunge zoom e crea un video rifinito.',
  'createProject.recordTitle': 'Registra lo schermo e ottieni un video rifinito',
  'createProject.renewPlan': 'Rinnova il piano',
  'createProject.sampleDescription':
    'Abbiamo già inserito i dettagli del video di esempio. Scegli la lingua in cui vuoi tradurlo.',
  'createProject.selectLanguages': 'Seleziona la lingua originale e la lingua in cui vuoi tradurre',
  'createProject.selectTargetLanguage': 'Seleziona la lingua di destinazione della traduzione',
  'createProject.translate': 'Traduci',
  'createProject.translateCardTitle': 'Traduci video o audio',
  'createProject.translateDescription': 'Carica un file video o audio e traducilo in oltre 165 lingue e dialetti.',
  'createProject.translateFirstMinute': 'Localizzeremo il primo minuto del tuo video.',
  'createProject.translateFirstMinuteRest': 'Può essere più lungo: per ora useremo il primo minuto.',
  'createProject.translateNow': 'Traduci ora',
  'createProject.translateRecording': 'Traduci la registrazione',
  'createProject.translateTo': 'Traduci in',
  'createProject.trialUploadDescription':
    'Carica un file o incolla un link, scegli una lingua e al resto pensiamo noi.',
  'createProject.trySample': 'prova un video di esempio',
  'createProject.trySampleVideo': 'Non hai una clip a portata di mano? Prova un video di esempio',
  'createProject.upgradePlan': 'Passa a un piano superiore',
  'createProject.uploadFile': 'Fai clic per caricare un file',
  'createProject.uploadTitle': 'Carica un video o un audio da tradurre',
  'createProject.videoLeft': 'Rimane {count} video',
  'createProject.videoName': 'Nome del video',
  'createProject.videoNames': 'Nomi dei video',
  'createProject.videosLeft': 'Rimangono {count} video',
  'createProject.videoSpeakers': 'Numero di persone che parlano nel video',
  'createProject.zeroDescription':
    'Ottieni sottotitoli precisi, una voce fuori campo naturale e una sincronizzazione perfetta, tutto in un unico strumento',
  'createProject.zeroTitle': 'Traduci video o audio in oltre 135 lingue',
  ...ACCOUNT_TRANSLATIONS.it,
  ...AUTH_TRANSLATIONS.it,
  ...BOOSTERS_TRANSLATIONS.it,
  ...CREATE_PROJECT_TRANSLATIONS.it,
  ...EDITOR_TRANSLATIONS.it,
  ...HOME_TRANSLATIONS.it,
  ...PRICING_TRANSLATIONS.it,
  ...SHELL_TRANSLATIONS.it,
  ...TRIAL_TRANSLATIONS.it,
  ...TRIAL_PAYWALL_TRANSLATIONS.it,
  ...VOICE_TRANSLATIONS.it,
};

const RU_TRANSLATIONS: TranslationDictionary = {
  'account.settings': 'Настройки аккаунта',
  'common.advancedTools': 'Дополнительные инструменты',
  'common.back': 'Назад',
  'common.desktopOnly': 'Только на компьютере',
  'common.fieldRequired': 'Обязательное поле',
  'common.free': 'Бесплатно',
  'common.hideSidebar': 'Скрыть боковую панель',
  'common.lockSidebar': 'Закрепить боковую панель',
  'common.logout': 'Выйти',
  'common.navigationSidebar': 'Панель навигации',
  'common.openSidebar': 'Открыть боковую панель',
  'common.or': 'или',
  'common.profile': 'Профиль',
  'common.profileImage': 'Изображение профиля',
  'common.serverError': 'Ошибка сервера. Попробуйте позже',
  'header.myProfile': 'Мой профиль',
  'header.profileAndApi': 'Профиль и API',
  'home.newFolder': 'Новая папка',
  'home.projects': 'Проекты',
  'home.refreshPage': 'Обновить страницу',
  'home.searchVideos': 'Поиск по вашим видео',
  'home.somethingWentWrong': 'Что-то пошло не так',
  'home.translateVideoAndAudio': 'Перевод видео и аудио',
  'home.trial.day': 'День {currentDay} из {totalDays}',
  'home.trial.dayAria': 'Пробный период: день {currentDay} из {totalDays}',
  'home.trial.dayLeft': 'Остался {count} день',
  'home.trial.daysLeft': 'Ещё {count} дн. пробного периода',
  'home.trial.description': 'Переведите первое видео. Если понадобится помощь, начните чат в любое время.',
  'home.trial.finalDay': 'Последний день пробного периода',
  'home.trial.noCreditCard': 'Без банковской карты и обязательств.',
  'home.trial.videoLeft': 'Осталось {count} видео',
  'home.trial.videosLeft': 'Осталось {count} видео',
  'home.trial.welcome': 'Добро пожаловать в Rask',
  'home.tryRefresh': 'Пожалуйста, обновите страницу',
  'language.english': 'Английский',
  'language.french': 'Французский',
  'language.italian': 'Итальянский',
  'language.label': 'Язык интерфейса',
  'language.portuguese': 'Португальский (Бразилия)',
  'language.portuguesePortugal': 'Португальский (Португалия)',
  'language.russian': 'Русский',
  'language.spanish': 'Испанский',
  'sidebar.bookCall': 'Записаться на звонок с нашей командой',
  'sidebar.dictionary': 'Глоссарий',
  'sidebar.getHelp': 'Помощь',
  'sidebar.helpCenter': 'Справочный центр',
  'sidebar.settingsMembers': 'Настройки',
  'sidebar.translation': 'Перевод',
  'sidebar.viewPlans': 'Посмотреть тарифы',
  'sidebar.voiceCloneLibrary': 'Voice Clone',
  'createProject.add': 'Добавить',
  'createProject.autodetect': 'Определить автоматически',
  'createProject.choose': 'Выбрать',
  'createProject.chooseFile': 'Нажмите, чтобы выбрать файл',
  'createProject.chooseFiles': 'Нажмите, чтобы выбрать файлы (не более {count})',
  'createProject.dragIt': 'или перетащите его сюда',
  'createProject.dragThem': 'или перетащите их сюда',
  'createProject.dropFiles': 'Перетащите файлы сюда или нажмите, чтобы загрузить',
  'createProject.fillDetails': 'Укажите параметры проекта',
  'createProject.fillDetailsAndUpload': 'Укажите параметры проекта и загрузите файлы',
  'createProject.firstVideoDescription': 'Ваше видео — на любом языке.',
  'createProject.firstVideoTitle': 'Локализуйте первое видео',
  'createProject.freeVideoLeft': '{count} бесплатное видео',
  'createProject.freeVideosLeft': '{count} бесплатных видео',
  'createProject.localize': 'Локализовать',
  'createProject.localizeInto': 'Перевести на',
  'createProject.localizeNow': 'Перевести сейчас',
  'createProject.mainTool': 'Основной инструмент',
  'createProject.newProject': 'Новый проект',
  'createProject.newTranslation': 'Новый перевод',
  'createProject.originalLanguage': 'Исходный язык',
  'createProject.pasteLink': 'Вставьте ссылку на YouTube или Google Drive',
  'createProject.pasteLinkWithVimeo': 'Вставьте ссылку на YouTube, Google Drive или Vimeo',
  'createProject.projectName': 'Название проекта',
  'createProject.recordDescription':
    'Запишите экран и видео с камеры. Rask очистит речь, добавит приближения и создаст готовое видео.',
  'createProject.recordTitle': 'Запишите экран и получите готовое видео',
  'createProject.renewPlan': 'Возобновить подписку',
  'createProject.sampleDescription':
    'Мы уже указали параметры тестового видео. Просто выберите язык, на который хотите его перевести.',
  'createProject.selectLanguages': 'Выберите исходный язык видео и язык перевода',
  'createProject.selectTargetLanguage': 'Выберите язык перевода видео',
  'createProject.translate': 'Перевести',
  'createProject.translateCardTitle': 'Перевести видео или аудио',
  'createProject.translateDescription':
    'Загрузите любой видео- или аудиофайл и переведите его на один из 165+ языков и диалектов.',
  'createProject.translateFirstMinute': 'Локализуем первую минуту вашего видео.',
  'createProject.translateFirstMinuteRest': 'Видео может быть длиннее — сейчас мы используем только первую минуту.',
  'createProject.translateNow': 'Перевести сейчас',
  'createProject.translateRecording': 'Перевести запись',
  'createProject.translateTo': 'Перевести на',
  'createProject.trialUploadDescription':
    'Загрузите файл или вставьте ссылку, выберите язык — остальное мы сделаем сами.',
  'createProject.trySample': 'попробуйте пример',
  'createProject.trySampleVideo': 'Нет подходящего файла? Попробуйте тестовое видео',
  'createProject.upgradePlan': 'Улучшить тариф',
  'createProject.uploadFile': 'Нажмите, чтобы загрузить файл',
  'createProject.uploadTitle': 'Загрузите видео или аудио для перевода',
  'createProject.videoLeft': 'Осталось {count} видео',
  'createProject.videoName': 'Название видео',
  'createProject.videoNames': 'Названия видео',
  'createProject.videosLeft': 'Осталось {count} видео',
  'createProject.videoSpeakers': 'Количество спикеров в видео',
  'createProject.zeroDescription': 'Чистые субтитры, естественная озвучка и точный тайминг — в одном инструменте',
  'createProject.zeroTitle': 'Переведите видео или аудио на 135+ языков',
  ...ACCOUNT_TRANSLATIONS.ru,
  ...AUTH_TRANSLATIONS.ru,
  ...BOOSTERS_TRANSLATIONS.ru,
  ...CREATE_PROJECT_TRANSLATIONS.ru,
  ...EDITOR_TRANSLATIONS.ru,
  ...HOME_TRANSLATIONS.ru,
  ...PRICING_TRANSLATIONS.ru,
  ...SHELL_TRANSLATIONS.ru,
  ...TRIAL_TRANSLATIONS.ru,
  ...TRIAL_PAYWALL_TRANSLATIONS.ru,
  ...VOICE_TRANSLATIONS.ru,
};

export const TRANSLATIONS = {
  en: EN_TRANSLATIONS,
  es: ES_TRANSLATIONS,
  fr: FR_TRANSLATIONS,
  it: IT_TRANSLATIONS,
  pt: PT_TRANSLATIONS,
  'pt-PT': PT_PT_TRANSLATIONS,
  ru: RU_TRANSLATIONS,
} as const;

const resources = Object.fromEntries(APP_LOCALES.map((locale) => [locale, { translation: TRANSLATIONS[locale] }]));

export const interfaceI18n = createInstance();

void interfaceI18n.init({
  fallbackLng: 'en',
  initAsync: false,
  interpolation: {
    escapeValue: false,
    prefix: '{',
    suffix: '}',
  },
  keySeparator: false,
  lng: getActiveInterfaceLocale(),
  nsSeparator: false,
  react: { useSuspense: false },
  resources,
  returnNull: false,
  returnObjects: false,
  supportedLngs: APP_LOCALES,
});

export const translate = (locale: AppLocale, key: TranslationKey, variables?: Record<string, number | string>) => {
  return interfaceI18n.t(key, { ...variables, lng: locale });
};
